import { useRouter } from 'next/router';
import { appendGOATandCountryCodeToTitle } from 'shared/utils/pageMetadataUtils';

const getBrandName = (filters, brandSlug) => {
  const defaultBrandName = brandSlug.replaceAll('-', ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  if (!filters?.brandsList) {
    return defaultBrandName;
  }

  const brand = filters.brandsList?.find((b) => b.slug === brandSlug);

  if (brand) {
    return brand?.name;
  }

  return defaultBrandName;
};

export const usePageMetadataCS = ({
  mainTitle = '',
  defaultTitle = '',
  mainDescription = '',
  consumerSearchData = {},
}: {
  mainTitle?: string;
  defaultTitle?: string;
  mainDescription?: string;
  consumerSearchData?: any;
}) => {
  const { locale, pathname, query } = useRouter();
  let pageTitle = appendGOATandCountryCodeToTitle(mainTitle || defaultTitle, locale);
  let pageDescription = mainDescription;

  // Collections: /collections/[slug]
  if (pathname.includes('collections') && query?.slug) {
    const collectionName = query?.slug?.[0]
      ?.replace(/-/g, ' ')
      ?.replace(/_/g, ' ')
      ?.replace(/\b\w/g, (c) => c.toUpperCase());
    pageTitle = appendGOATandCountryCodeToTitle(defaultTitle, locale);
    pageDescription =
      mainDescription ||
      `Shop the curated ${mainTitle || collectionName} collection on GOAT. Authenticity assured.`;
  }

  // Brands: /brand/[slug]
  if (pathname.includes('brand') && query?.slug) {
    const brandName = getBrandName(consumerSearchData?.aggregations?.filters, query?.slug);
    const title = `Buy ${brandName}: New & Iconic Styles`;
    pageTitle = appendGOATandCountryCodeToTitle(title, locale);
    pageDescription =
      mainDescription ||
      `Explore the past, present and future of ${brandName} on GOAT. Authenticity Assured.`;
  }

  // Timeline
  if (pathname.includes('timeline')) {
    const year = query?.timelineYear?.[0];

    if (year && year !== 'future') {
      pageTitle = appendGOATandCountryCodeToTitle(`${year} Sneaker Releases`, locale);
      pageDescription = `Shop sneaker releases from ${year} on GOAT. Authenticity assured.`;
    } else {
      pageTitle = appendGOATandCountryCodeToTitle('Sneaker Release Calendar', locale);
      pageDescription = `Discover a sneaker release calendar on GOAT. Featuring new & upcoming styles. Authenticity assured.`;
    }
  }

  // [pageSlug] paths: brand, color, silhouette
  if (query?.pageSlug && Object.keys(query).length > 1) {
    if (query?.brandName) {
      const brandName = getBrandName(consumerSearchData?.aggregations?.filters, query?.brandName);
      const category = (query.pageSlug as string)
        ?.replaceAll('sneakers', 'shoes')
        .replace(/-/g, ' ')
        .replace(/_/g, ' ');
      const subCategory = (query.subCategory as string)?.replaceAll(/-/g, ' ');

      if (subCategory) {
        if (category === 'shoes') {
          pageTitle = appendGOATandCountryCodeToTitle(
            mainTitle || `Buy ${brandName} ${subCategory} ${category}: New & Pre-Owned`,
            locale,
          );
          pageDescription =
            mainDescription ||
            `Explore the past, present and future of ${brandName} ${subCategory} ${category} on GOAT. Authenticity Assured.`;
        } else {
          pageTitle = appendGOATandCountryCodeToTitle(
            mainTitle || `Buy ${brandName} ${subCategory}: New & Pre-Owned`,
            locale,
          );
          pageDescription =
            mainDescription ||
            `Explore the past, present and future of ${brandName} ${subCategory} on GOAT. Authenticity Assured.`;
        }
      } else {
        pageTitle = appendGOATandCountryCodeToTitle(
          mainTitle || `Buy ${brandName} ${category}: New & Pre-Owned`,
          locale,
        );
        pageDescription =
          mainDescription ||
          `Explore the past, present and future of ${brandName} ${category} on GOAT. Authenticity Assured.`;
      }
    }

    if (query?.silhouette) {
      const normalizedSilhouetteValue = (query?.silhouette as string)
        ?.replace(/-/g, ' ')
        ?.replace(/_/g, ' ')
        ?.replace(/\b\w/g, (c) => c.toUpperCase());
      pageTitle = appendGOATandCountryCodeToTitle(
        mainTitle || `Buy ${normalizedSilhouetteValue}: New Releases & Iconic Styles`,
        locale,
      );
      pageDescription =
        mainDescription ||
        `Shop ${normalizedSilhouetteValue} on GOAT. Featuring new, upcoming and iconic styles. Authenticity assured.`;
    }

    if (query?.colorValue) {
      const category = (query.pageSlug as string)?.replaceAll('sneakers', 'shoes');
      const isShoes = category === 'shoes';
      const color = query?.colorValue as string;
      pageTitle = appendGOATandCountryCodeToTitle(mainTitle || `Buy ${color} ${category}`, locale);

      if (isShoes) {
        pageDescription =
          mainDescription ||
          `Shop ${color} ${category} on GOAT including iconic styles and upcoming and recent releases.`;
      } else {
        pageDescription =
          mainDescription ||
          `Shop ${color} ${category} on GOAT. Explore new releases & iconic styles. Authenticity assured.`;
      }
    }
  }

  // Designer: sneakers/designer/[designerValue]
  if (query?.designerValue) {
    const designerName = (query?.designerValue as string)
      ?.replace(/-/g, ' ')
      ?.replace(/_/g, ' ')
      ?.replace(/\b\w/g, (c) => c.toUpperCase());
    pageTitle = appendGOATandCountryCodeToTitle(
      mainTitle || `Buy ${designerName} Shoes: New Releases & Iconic Styles`,
      locale,
    );
    pageDescription =
      mainDescription || `Shop shoes designed by ${designerName} on GOAT. Authenticity assured.`;
  }

  // Upper Material: sneakers/upper-material/[upperMaterialValue]
  if (query?.upperMaterialValue) {
    const upperMaterialName = (query?.upperMaterialValue as string)
      ?.replace(/-/g, ' ')
      ?.replace(/_/g, ' ')
      ?.replace(/\b\w/g, (c) => c.toUpperCase());
    pageTitle = appendGOATandCountryCodeToTitle(
      mainTitle || `Buy ${upperMaterialName} shoes`,
      locale,
    );
    pageDescription =
      mainDescription || `Shop ${upperMaterialName} shoes on GOAT. Authenticity assured.`;
  }

  // Technology: sneakers/technology/[technologyValue]
  if (query?.technologyValue) {
    const technologyName = (query?.technologyValue as string)
      ?.replace(/-/g, ' ')
      ?.replace(/_/g, ' ')
      ?.replace(/\b\w/g, (c) => c.toUpperCase());
    pageTitle = appendGOATandCountryCodeToTitle(
      mainTitle || `Buy shoes featuring ${technologyName} technology`,
      locale,
    );
    pageDescription =
      mainDescription ||
      `Shop shoes featuring ${technologyName} technology on GOAT. Authenticity assured.`;
  }

  // Top level categories: /sneakers, /apparel, /bags
  const topLevelCategories = new Set([
    'art',
    'sneakers',
    'apparel',
    'accessories',
    'bags',
    'jewelry',
    'space',
    'collectibles',
  ]);
  const nameOfPath = pathname?.split('/');
  const isCategoryBasePath =
    nameOfPath?.length &&
    nameOfPath[nameOfPath.length - 1] &&
    topLevelCategories.has(nameOfPath[nameOfPath.length - 1]);

  if (isCategoryBasePath) {
    const parentCategory = nameOfPath[nameOfPath.length - 1];
    const categoryTodisplay = parentCategory === 'sneakers' ? 'shoes' : parentCategory;
    pageTitle = appendGOATandCountryCodeToTitle(
      mainTitle || `Buy New & Iconic ${categoryTodisplay}`,
      locale,
    );
    pageDescription =
      mainDescription ||
      `Shop ${categoryTodisplay} on GOAT. Featuring new releases and iconic styles. Authenticity Assured.`;
  }

  // Top Level Category with subcategory: /sneakers/basketball, /apparel/tops
  const isCategoryWithSubcategoryPath =
    nameOfPath?.length &&
    nameOfPath?.length === 3 &&
    topLevelCategories.has(nameOfPath[nameOfPath.length - 2]);

  if (isCategoryWithSubcategoryPath) {
    const isShoes = nameOfPath[nameOfPath.length - 2] === 'sneakers';
    const subCategory = nameOfPath[nameOfPath.length - 1];
    const subCategoryDisplay = subCategory.includes('[')
      ? (query?.subCategory as string)?.replace(/-/g, ' ').replace(/_/g, ' ')
      : subCategory;

    if (isShoes) {
      pageTitle = appendGOATandCountryCodeToTitle(mainTitle || `Buy ${subCategory} shoes`, locale);
      pageDescription =
        mainDescription ||
        `Shop ${subCategory} shoes on GOAT. Featuring new releases and iconic styles. Authenticity Assured.`;
    } else {
      pageTitle = appendGOATandCountryCodeToTitle(
        mainTitle || `Buy ${subCategoryDisplay}: Streetwear & Luxury`,
        locale,
      );
      pageDescription =
        mainDescription ||
        `Shop ${subCategoryDisplay} on GOAT. Featuring new releases & iconic styles. Authenticity Assured.`;
    }
  }

  return { pageTitle, pageDescription };
};
